import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { fmtDateTime, melbourneMonthKey, monthLabel, shiftMonth } from "@/lib/date";
import { STAGES } from "@/lib/constants";
import { StageBadge } from "@/components/Badges";
import { ChevronLeft, ChevronRight, CalendarClock, Rocket, UserX, UserPlus, TrendingUp } from "lucide-react";
import { Card } from "@/components/ui/card";
import ScopeSwitch from "@/components/ScopeSwitch";

export default function Dashboard() {
  const { scopes } = useAuth();
  const [scope, setScope] = useState(null);
  const [month, setMonth] = useState(melbourneMonthKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!scopes?.length) return;
    if (!scope) {
      // default: admins -> all, others -> team if available else personal
      setScope(scopes.includes("all") ? "all" : (scopes.includes("team") ? "team" : "personal"));
    }
  }, [scopes, scope]);

  useEffect(() => {
    if (!scope) return;
    setLoading(true);
    api.get("/dashboard", { params: { month, scope } })
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [month, scope]);

  const counts = data?.counts || {};
  const stageBreakdown = data?.stage_breakdown || {};
  const maxStage = useMemo(() => Math.max(1, ...STAGES.map((s) => stageBreakdown[s] || 0)), [stageBreakdown]);
  const upcoming = data?.upcoming_meetings || [];
  const topCoaches = data?.top_coaches || [];

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.24em] text-[#C9A961] font-semibold">Overview</div>
          <h1 className="font-serif text-3xl sm:text-4xl text-[#0A1F44] mt-1">Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">Melbourne · {monthLabel(month)}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ScopeSwitch value={scope} onChange={setScope} scopes={scopes} />
          <div className="flex items-center gap-1 bg-white rounded-full border border-slate-200 p-1 shadow-sm">
            <button data-testid="month-prev" onClick={() => setMonth((m) => shiftMonth(m, -1))} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="text-sm font-medium text-slate-800 px-2 min-w-[110px] text-center">{monthLabel(month)}</div>
            <button data-testid="month-next" onClick={() => setMonth((m) => shiftMonth(m, 1))} className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center">
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <KPI label="Active in process" value={counts.active} icon={TrendingUp} accent="navy" testId="kpi-active" />
        <KPI label="Launches this month" value={counts.launched_month} icon={Rocket} accent="gold" testId="kpi-launched" />
        <KPI label="New this month" value={counts.new_month} icon={UserPlus} accent="navy" testId="kpi-new" />
        <KPI label="Dropped this month" value={counts.dropped_month} icon={UserX} accent="rust" testId="kpi-dropped" />
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <Card className="p-5 lg:col-span-2 border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-serif text-lg text-[#0A1F44]">Pipeline breakdown</h2>
            <span className="text-xs text-slate-500">Active PBOs by stage</span>
          </div>
          <div className="space-y-2.5" data-testid="stage-breakdown">
            {STAGES.map((s) => {
              const v = stageBreakdown[s] || 0;
              const pct = (v / maxStage) * 100;
              return (
                <div key={s} className="flex items-center gap-3">
                  <div className="w-24 sm:w-28"><StageBadge stage={s} /></div>
                  <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full bg-[#0A1F44] rounded-full transition-all" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="w-8 text-right text-sm font-semibold text-slate-800 tabular-nums">{v}</div>
                </div>
              );
            })}
          </div>
          {data?.conversion_rate != null && (
            <div className="mt-5 pt-4 border-t border-slate-100 flex items-baseline justify-between">
              <div className="text-sm text-slate-600">All-time conversion (Launched vs. Dropped)</div>
              <div className="font-serif text-2xl text-[#0A1F44]" data-testid="conversion-rate">{data.conversion_rate}%</div>
            </div>
          )}
          {topCoaches.length > 0 && (
            <div className="mt-5 pt-4 border-t border-slate-100">
              <div className="text-sm text-slate-600 mb-2">Top coaches by completed appointments</div>
              <div className="flex flex-wrap gap-2">
                {topCoaches.map((c) => (
                  <span key={c.coach} className="text-xs px-2.5 py-1 rounded-full bg-[#F4EFE1] text-[#6B5A1D] border border-[#E4D8B0]">
                    {c.coach} · <span className="font-semibold">{c.count}</span>
                  </span>
                ))}
              </div>
            </div>
          )}
        </Card>

        <Card className="p-5 border-slate-200">
          <div className="flex items-center gap-2 mb-4">
            <CalendarClock className="w-4 h-4 text-[#C9A961]" />
            <h2 className="font-serif text-lg text-[#0A1F44]">Upcoming meetings</h2>
          </div>
          <div className="space-y-3" data-testid="upcoming-list">
            {loading && <div className="text-sm text-slate-500">Loading…</div>}
            {!loading && upcoming.length === 0 && <div className="text-sm text-slate-500">No meetings scheduled.</div>}
            {upcoming.map((m) => (
              <Link key={m.pbo_id + m.next_meeting_at} to={`/pbos/${m.pbo_id}`} data-testid={`upcoming-${m.pbo_id}`} className="block group">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-900 truncate group-hover:text-[#0A1F44]">{m.pbo_name}</div>
                    <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                      <span>{m.ibo_partner_name || m.upline_team}</span>
                      <span>·</span>
                      <StageBadge stage={m.next_meeting_stage || m.current_stage} />
                    </div>
                  </div>
                  <div className="text-right text-xs text-slate-600 whitespace-nowrap">{fmtDateTime(m.next_meeting_at)}</div>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

function KPI({ label, value, icon: Icon, accent, testId }) {
  const accents = {
    navy: "bg-[#0A1F44] text-[#C9A961]",
    gold: "bg-[#C9A961] text-[#0A1F44]",
    rust: "bg-[#8A1F1F] text-white",
  };
  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid={testId}>
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accents[accent] || accents.navy}`}>
          <Icon className="w-4 h-4" />
        </div>
        <div className="min-w-0">
          <div className="text-xs text-slate-500 leading-tight">{label}</div>
          <div className="font-serif text-2xl text-[#0A1F44] tabular-nums leading-tight">{value ?? "—"}</div>
        </div>
      </div>
    </Card>
  );
}
